import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faBell, faBook, faComputer, faFutbol, faGuitar, faHeart, faMoneyBill, faMusic, faNewspaper, faStarOfDavid, faTrophy } from "@fortawesome/free-solid-svg-icons"
import { ICategory, IProgram, IRegion, IShows, ISongs, IStation, ITrack, IType } from "@models"
import { PLAYLIST_ANCHOR, playlistTracks } from "@lib/playlist"
import { IMetaData } from "@types"
import dayjs from "dayjs"
import { Metadata } from "next"

export const Logger = {
    log: (message?: any, ...optionalParams: any[]) => {
        console.log(`${message} `, ...optionalParams)
    },
    error: (message?: any, ...optionalParams: any[]) => {
        console.log(`\u001b[1;41m ${message}`, ...optionalParams, "\u001b[0m")
    },
    warn: (message?: any, ...optionalParams: any[]) => {
        console.log(`\u001b[1;43m ${message}`, ...optionalParams, "\u001b[0m")
    },
    info: (message?: any, ...optionalParams: any[]) => {
        console.log(`\u001b[1;44m ${message}`, ...optionalParams, "\u001b[0m")
    },
}
export const getProgramImage = (program: IProgram, station: IStation) => {
    if (program?.image)
        return program.image
    return `/images/stations/${station.id}.webp`
}

export const TOP_AMOUNT = 25
export const __DEV__ = process.env.NODE_ENV === 'development'
export const CATEGORIES: { [key in ICategory]: {
    name: string
    icon: IconProp
    snipped: string
    title?: string
    description?: string
}} = {
    favorites: {
        name: "המועדפים שלי",
        icon: faHeart,
        title: "תחנות רדיו ישראלי מועדפות",
        description: "התחנות שסימנתם כמועדפות ברדיו הד, שמורות במקום אחד לגישה מהירה.",
        snipped: `התחנות שסימנתם בלב נשמרות כאן, כדי שתוכלו לחזור אליהן ישר בלי לחפש. הרשימה נשמרת בדפדפן שלכם ולא דורשת הרשמה.`
    },
    top: {
        name: `${TOP_AMOUNT} המובילות`,
        icon: faTrophy,
        title: `${TOP_AMOUNT} התחנות המובילות ברדיו הד`,
        description: `האזינו ל${TOP_AMOUNT} התחנות המובילות ברדיו הד - תחנות הרדיו הפופולריות ביותר באתר.`,
        snipped: `${TOP_AMOUNT} התחנות שהכי מאזינים להן באתר, לפי סדר הפופולריות: גלגלצ, רשת ב', רשת ג', גלי צה"ל ועוד. כולן בשידור חי, וזו רשימה טובה להתחיל ממנה.`
    },
    news: {
        name: "חדשות",
        icon: faNewspaper,
        title: "תחנות רדיו חדשות ואקטואליה",
        description: `כל תחנות הרדיו לחדשות ואקטואליה בשידור חי - רשת ב', גלי צה"ל, 103FM ואקו 99. מהדורות בראש כל שעה, ראיונות ופרשנות, האזנה אונליין בחינם.`,
        snipped: `מהדורות בראש כל שעה, דיווחים מהשטח ותוכניות ראיונות לאורך היום. בקטגוריה יש את רשת ב', גלי צה"ל, 103FM ואקו 99, וגם תחנות אזוריות שמביאות את החדשות המקומיות של האזור שלהן. הכל בשידור חי, בלי עיכוב.`
    },
    music: {
        name: "מוזיקה",
        icon: faMusic,
        title: "תחנות רדיו מוזיקה",
        description: `תחנות הרדיו המוזיקליות של ישראל בשידור חי - 88FM, גלגלצ, רדיו תל אביב וקול המוסיקה. פופ, רוק, ג'אז וקלאסי בהאזנה אונליין בחינם.`,
        snipped: `הקטגוריה הגדולה באתר, עם תחנות מכל הסגנונות: פופ ורוק ישראלי, לועזי ואלטרנטיבי, ג'אז ומוזיקה קלאסית. כאן יש את 88FM, גלגלצ, רדיו תל אביב, רדיוס וקול המוסיקה, וגם עשרות תחנות אינטרנטיות שמשדרות ז'אנר אחד בלבד.`
    },
    sport: {
        name: "ספורט",
        icon: faFutbol,
        title: "תחנות רדיו ספורט",
        description: `תחנות רדיו ספורט בשידור חי - 103FM, רדיו תל אביב, רדיו חיפה ורדיו 5. שידורי כדורגל וכדורסל, פרשנות ותוצאות בהאזנה אונליין בחינם.`,
        snipped: `שידורים חיים ממגרשי הכדורגל והכדורסל, פרשנות אחרי המשחק ותוכניות יומיות על ליגת העל, היורוליג והנבחרות. התחנות הבולטות כאן הן 103FM, רדיו תל אביב, רדיו חיפה ורדיו 5. רוב השידורים החיים הם בסופי שבוע ובערבי משחקים, ותוכניות הדיבור רצות כל השבוע.`
    },
    mizrahit: {
        name: "מזרחית / ים תיכונית",
        icon: faGuitar,
        title: "רדיו מזרחית - כל התחנות המזרחיות בשידור חי",
        description: `רדיו מזרחית אונליין בחינם - כל תחנות הרדיו המזרחיות והים תיכוניות בשידור חי: חם אש, גלגלצ ים תיכוני, קצב ים תיכוני, קול הים התיכון ורדיו מנטה.`,
        snipped: `מוזיקה מזרחית וים תיכונית מסביב לשעון, להיטים חדשים לצד קלאסיקות של הז'אנר. הקטגוריה כוללת את חם אש, קצב ים תיכוני, קול הים התיכון, התחנה ורדיו אילת. רובן תחנות אינטרנטיות שמשדרות סגנון אחד בלבד, בלי מהדורות ובלי הפסקות דיבור.`
    },
    religion: {
        name: "דת",
        icon: faStarOfDavid,
        title: "תחנות רדיו דת ומסורת",
        description: `תחנות רדיו דת ומסורת בשידור חי - מורשת, קול ברמה, קול חי וערוץ שבע. שיעורי תורה, דף יומי ומוזיקה יהודית בהאזנה אונליין בחינם.`,
        snipped: `שיעורי תורה, דף יומי, הלכה יומית ומוזיקה יהודית לאורך כל היום. בקטגוריה יש את מורשת, קול ברמה, קול חי, ערוץ שבע והמוזיקה היהודית, וגם ערוצי ברסלב הייעודיים לדעת רבנו, להלכות ולדף היומי. חלק מהתחנות לא משדרות בשבת ובחגים ומצטרפות להגל השקט.`
    },
    education: {
        name: "חינוך",
        icon: faBook,
        title: "תחנות רדיו חינוך וקהילה",
        description: `תחנות רדיו חינוך וקהילה בשידור חי - קול הקמפוס, קול הנגב, קול יזרעאל ואורנים. תוכן סטודנטיאלי ותרבותי בהאזנה אונליין בחינם.`,
        snipped: `תחנות הקמפוסים והקהילות: קול הקמפוס, קול הנגב, קול יזרעאל, אורנים וקול השפלה. את רוב התוכניות עושים סטודנטים ומתנדבים, והן משלבות תרבות, תכנים מקומיים ומוזיקה שלא תשמעו בתחנות המסחריות. הרבה שדרנים מוכרים התחילו כאן.`
    },
    economy: {
        name: "כלכלה",
        icon: faMoneyBill,
        title: "תחנות רדיו כלכלה ועסקים",
        description: `תחנות רדיו כלכלה ועסקים בשידור חי - 103FM, רשת א', עסקים ורדיו חיפה. שוק ההון, נדל"ן וחדשות כלכליות בהאזנה אונליין בחינם.`,
        snipped: `שוק ההון, הנדל"ן, ההייטק והצרכנות, עם סקירות בוקר, ראיונות עם מנהלים ופרשנות שוטפת על המסחר. התחנות המרכזיות כאן הן 103FM, רשת א', עסקים ורדיו חיפה. רוב התוכניות משודרות בשעות המסחר בבורסה בתל אביב, בין הבוקר לאחר הצהריים.`
    },
    silence: {
        name: "הגל השקט",
        icon: faBell,
        title: "הגל השקט - תחנות רדיו לשעת חירום",
        description: `הגל השקט - תחנות הרדיו שפורצות לשידור רק בזמן אזעקת אמת: גלי ישראל, מורשת, קול חי, קול ברמה ורדיו דרום. האזנה אונליין בחינם.`,
        snipped: `הגל השקט הוא הכינוי לתחנות שלא משדרות בשבת ובחגים, ופורצות לשידור רק כשיש אזעקת אמת, כדי להעביר את הנחיות פיקוד העורף. בקטגוריה יש את גלי ישראל, מורשת, קול חי, קול ברמה, רדיו דרום ו-90FM אמצע הדרך. בשאר הזמן השידור שקט.`
    }
}
export const TYPES: { [key in IType]: {
    name: string
    order: number
    snipped: string
    title?: string
    description?: string
    icon?: IconProp
} } = {
    national: {
        name: "ארצי",
        order: 1,
        title: "תחנות הרדיו הארציות בישראל",
        description: `כל תחנות הרדיו הארציות בשידור חי - גלגלצ, רשת ב', רשת ג', גלי צה"ל וכאן 88. האזנה אונליין בחינם מכל מקום בארץ ובעולם.`,
        snipped: `התחנות הארציות נשמעות כמעט בכל נקודה בישראל: גלגלצ, רשת ב', רשת ג', גלי צה"ל וכאן 88. רובן שייכות לתאגיד השידור הציבורי או לגלי צה"ל, ומשלבות חדשות, מוזיקה ותוכניות דיבור לאורך היום. בהאזנה אונליין אין צורך לחפש תדר, השידור זהה בכל מקום.`
    },
    regional: {
        name: "אזורי",
        order: 2,
        title: "תחנות הרדיו האזוריות בישראל",
        description: `תחנות הרדיו האזוריות בשידור חי - 103FM, אקו 99, רדיו תל אביב, רדיו ירושלים ורדיו דרום. חדשות מקומיות ומוזיקה בהאזנה אונליין בחינם.`,
        snipped: `התחנות האזוריות קיבלו רישיון שידור לאזור מסוים, והן משלבות מוזיקה, חדשות מקומיות ותוכניות דיבור על מה שקורה באזור. בין הבולטות: 103FM ורדיו תל אביב במרכז, אקו 99 ורדיו חיפה בצפון, רדיו ירושלים בירושלים ורדיו דרום בנגב. אונליין אפשר לשמוע אותן גם מחוץ לאזור הרישוי, שם ה-FM לא נקלט.`
    },
    university: {
        name: "אקדמאי",
        order: 4,
        title: "תחנות הרדיו האקדמיות בישראל",
        description: `תחנות הרדיו האקדמיות בשידור חי - קול הקמפוס, קול הנגב, רדיו BGU ואורנים. תוכן סטודנטיאלי ומוזיקה אלטרנטיבית בהאזנה אונליין בחינם.`,
        snipped: `התחנות האקדמיות משדרות מתוך האוניברסיטאות והמכללות, ואת רוב התוכן עושים סטודנטים. בקטגוריה יש את קול הקמפוס, קול הנגב, רדיו BGU, אורנים וקול יזרעאל. הפלייליסטים שלהן נועזים יותר מאלה של התחנות המסחריות, והרבה שדרנים ועיתונאים התחילו בהן.`
    },
    internet: {
        name: "אינטרנטי",
        order: 5,
        title: "תחנות רדיו אינטרנט בישראל",
        description: `תחנות רדיו אינטרנטיות בשידור חי - חם אש, המוזיקה היהודית, רדיו 5 ועסקים. עשרות תחנות נישה ללא תדר FM, בהאזנה אונליין בחינם.`,
        snipped: `התחנות האינטרנטיות משדרות ברשת בלבד, בלי תדר FM ובלי רישיון שידור, ולכן יש בהן את המגוון הגדול ביותר. הקטגוריה כוללת את חם אש, המוזיקה היהודית, רדיו 5, עסקים ורדיו המזרח. רובן משדרות סגנון אחד בלבד: מזרחית, דאנס, מוזיקה יהודית או תוכן כלכלי.`
    }
}

export const METADATA: IMetaData = {
    home: {
        title: `רדיו ישראלי לייב - שידור חי אונליין - ${process.env.NEXT_PUBLIC_SITE_NAME}`,
        description: "כל תחנות הרדיו הישראלי בשידור חי באתר רדיו הד. האזנה אונליין בחינם, בלי הרשמה ובלי התקנה. מוזיקה, חדשות ושידורים מכל הסוגים במקום אחד.",
    },
    notfound: {
        title: `העמוד לא נמצא - ${process.env.NEXT_PUBLIC_SITE_NAME}`,
        description: "העמוד שחיפשת לא נמצא - חפש אחרי תחנות רדיו אחרות באתר רדיו הד."
    },
    sitemap: {
        title: `מפת אתר רדיו ישראלי - ${process.env.NEXT_PUBLIC_SITE_NAME}`,
        description: "מפת אתר רדיו הד מפרטת את כל תחנות הרדיו המושמעות באתר, בנוסף למאמרים, תוכניות רדיו ועוד.",
    },
    search: {
        title: `חיפוש תחנות רדיו - ${process.env.NEXT_PUBLIC_SITE_NAME}`,
        description: "חיפוש תחנות רדיו באתר רדיו הד - חפשו תחנות רדיו לפי סוג וקטגוריה.",
    },
    terms: {
        title: `תנאי שימוש באתר רדיו הד - ${process.env.NEXT_PUBLIC_SITE_NAME}`,
        description: "תנאי שימוש באתר רדיו הד מגדירים את התנאים לשימוש באתר ובשירותיו.",
    },
    privacy: {
        title: `מדיניות פרטיות באתר רדיו הד - ${process.env.NEXT_PUBLIC_SITE_NAME}`,
        description: "מדיניות פרטיות באתר רדיו הד מגדירה את השימוש במידע האישי שנאסף באתר.",
    },
    contact: {
        title: `צור קשר עם רדיו הד - ${process.env.NEXT_PUBLIC_SITE_NAME}`,
        description: "צור קשר עם צוות רדיו הד בכל נושא שתרצו - וגם - בעלי תחנות רדיו מוזמנים לבקש להוסיף את התחנה שלהם לאתר.",
    },
    admin: {
        title: `ניהול תחנות רדיו - ${process.env.NEXT_PUBLIC_SITE_NAME}`,
        description: "ניהול תחנות הרדיו באתר רדיו הד - עריכת תחנות, סדר תחנות ועוד.",
    },
    frequencies: {
        title: `תדרים של תחנות רדיו - ${process.env.NEXT_PUBLIC_SITE_NAME}`,
        description: "כל תדרי השידור של תחנות גלגלצ, רשת ב, רשת ג, גלי ישראל, גלצ ועוד במקום אחד - וגם - האזנה אונליין מכל מקום בכל זמן.",
    },
}
type MetadataKey = keyof typeof METADATA;

export const getStaticMetadata = (key: MetadataKey): Metadata => {
    const url = `${process.env.NEXT_PUBLIC_URL}${key === 'home' ? '' : key + '/'}`
    return {
        ...METADATA[key],
        alternates: {
            canonical: url,
        },
        openGraph: {
            ...METADATA[key],
            url,
            images: [
                {
                  url: `${process.env.NEXT_PUBLIC_URL}images/logos/1200_1200.webp`,
                  width: 1200,
                  height: 1200,
                  alt: process.env.NEXT_PUBLIC_SITE_NAME
                },
                {
                  url: `${process.env.NEXT_PUBLIC_URL}images/logos/1200_675.webp`,
                  width: 1200,
                  height: 675,
                  alt: process.env.NEXT_PUBLIC_SITE_NAME
                }
            ]
        }
    }
}

export const numberWithCommas = (x: number = 0) => {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export const REGIONS: {[key in IRegion]: string} = {
    north: 'צפון',
    center: 'מרכז',
    south: 'דרום',
    jerusalem: 'ירושלים',
    east: 'מזרח'
}
export const STATION_FULL_NAME = (name: string) => {
    return name.includes("רדיו ")? name: `רדיו ${name}`
}

/** Falls back to a generated descriptor so list rows are never empty.
 *  Truncates `snippet` only as a last resort — it's prose, not a label.
 *  `omitFreq` is for lists that already carry the frequency in their own
 *  column, where leading with it again says nothing. */
export const STATION_BLURB = (station: IStation, { omitFreq }: { omitFreq?: boolean } = {}): string => {
    if (station.blurb) {
        // Blurbs are authored as "91.8FM · פופ ורוק ישראלי". Under `omitFreq`
        // the frequency half goes and the description stays — unless that
        // leaves nothing, in which case the blurb was only ever the frequency.
        const stripped = omitFreq
            ? station.blurb.replace(/^\s*\d+(\.\d+)?\s*fm\s*·\s*/i, '').trim()
            : station.blurb
        if (stripped) return stripped
    }

    // `freq` is inconsistent in the data: "106FM", "106.4FM", "106" and "" all appear.
    const freq = !omitFreq && station.freq?.trim()
        ? String(station.freq).replace(/\s*fm\s*$/i, '') + 'FM'
        : null
    const format = station.categories?.[0]
        ? CATEGORIES[station.categories[0]]?.name
        : TYPES[station.type]?.name

    const parts = [freq, format].filter(Boolean)
    if (parts.length) return parts.join(' · ')

    // Last resort. `snippet` is prose — take the first clause only, hard-capped.
    if (station.snippet) {
        const clause = station.snippet.split(/[.!?׃]/)[0].trim()
        return clause.length > 60 ? clause.slice(0, 57) + '…' : clause
    }
    return ''
}

/** One high-signal line for a station row in a category grid.
 *
 *  `STATION_BLURB` opens with a type label — "תחנת אינטרנט · …", "ערוץ דיגיטלי · …"
 *  — which is useful in a mixed list but pure noise in a category where most
 *  rows carry the same label. A real frequency is kept, because that *is* the
 *  distinguishing fact; the generic labels are dropped. */
const GENERIC_PREFIX = /^\s*(תחנת אינטרנט|תחנה אזורית|תחנה ארצית|ערוץ דיגיטלי|רדיו אוניברסיטאי)\s*·\s*/
export const STATION_CARD_LINE = (station: IStation): string => {
    const blurb = STATION_BLURB(station)
    const stripped = blurb.replace(GENERIC_PREFIX, '').trim()
    return stripped || blurb
}

/** The station's format, as a short label for the table below the hub. */
export const STATION_FORMAT = (station: IStation): string =>
    station.categories
        .filter(category => category !== 'top' && category !== 'favorites')
        .map(category => CATEGORIES[category]?.name)
        .filter(Boolean)
        .join(', ')

export const ORGANIZATION_LD_ID = `${process.env.NEXT_PUBLIC_URL}#organization`
export const WEBSITE_LD_ID = `${process.env.NEXT_PUBLIC_URL}#website`

export const ORGANIZATION_LD_JSON = () => {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": ORGANIZATION_LD_ID,
        "name": process.env.NEXT_PUBLIC_SITE_NAME,
        "url": process.env.NEXT_PUBLIC_URL,
        "logo": `${process.env.NEXT_PUBLIC_URL}images/logos/1200_1200.webp`,
        "sameAs": [
            "https://www.facebook.com/RadioHeadIsrael/"
        ],
        "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "support",
            "email": process.env.NEXT_PUBLIC_SUPPORT_EMAIL
        }
    }
}

export const WEBSITE_LD_JSON = () => {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": WEBSITE_LD_ID,
        "name": process.env.NEXT_PUBLIC_SITE_NAME,
        "url": process.env.NEXT_PUBLIC_URL,
        "inLanguage": "he-IL",
        "publisher": { "@id": ORGANIZATION_LD_ID },
        "potentialAction": {
            "@type": "SearchAction",
            "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${process.env.NEXT_PUBLIC_URL}search?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
        }
    }
}

export const STATIONS_LD_JSON = (stations: IStation[]) => {
    const jsonLDListItems = stations.map((station, index) => {
        const full_name = STATION_FULL_NAME(station.name)
        const desc = station.meta?.description || `מה ${full_name} מנגן עכשיו? ${full_name} אונליין באתר ${process.env.NEXT_PUBLIC_SITE_NAME} - היכנסו והאזינו לשידור החי של ${full_name} ולשאר תחנות הרדיו הישראליות בחינם וללא צורך בהתקנת תוכנה.`
        return {
          "@context":"http://schema.org",
          "@type": "ListItem",
          "position": index + 1,
          "url": `${process.env.NEXT_PUBLIC_URL}station/${station.id}/`,
          "name": station.name,
          "image": `${process.env.NEXT_PUBLIC_URL}images/stations/${station.id}.webp`,
          "description": desc
        }
    })
    const jsonLD = 
        {
          "@context":"http://schema.org",
          "@type":"ItemList",
          "name":"Stations",
          "numberOfItems": stations.length,
          "itemListElement": jsonLDListItems
        }
      
    return jsonLD
}

/** Generic FAQPage node. The station-page variant below builds its questions
 *  from frequency data; category hubs author theirs by hand, so they only need
 *  the wrapper. */
export const LD_FAQ = (items: { question: string, answer: string }[]) => {
    if (!items.length) return null
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map(({ question, answer }) => ({
            "@type": "Question",
            name: question,
            acceptedAnswer: {
                "@type": "Answer",
                text: answer
            }
        }))
    }
}

/** ItemList whose members are RadioStation nodes rather than bare ListItems.
 *  The generic `STATIONS_LD_JSON` describes each entry only by name, url and
 *  image; a category hub can say what the thing actually *is* — a radio station,
 *  on this frequency, with this logo — which is the part a crawler can reuse. */
export const CATEGORY_LD_STATIONS = (stations: IStation[], name: string) => ({
    "@type": "ItemList",
    name: `תחנות רדיו ${name}`,
    numberOfItems: stations.length,
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    itemListElement: stations.map((station, index) => {
        const url = `${process.env.NEXT_PUBLIC_URL}station/${station.id}/`
        const full_name = STATION_FULL_NAME(station.name)
        return {
            "@type": "ListItem",
            position: index + 1,
            item: {
                "@type": "RadioStation",
                "@id": url,
                name: full_name,
                url,
                logo: `${process.env.NEXT_PUBLIC_URL}images/stations/${station.id}.webp`,
                image: `${process.env.NEXT_PUBLIC_URL}images/stations/${station.id}.webp`,
                description: STATION_BLURB(station),
                inLanguage: "he",
                ...(station.freq? { broadcastFrequency: station.freq.replace(/\s*fm\s*$/i, ' FM') }: {}),
                broadcastServiceTier: "Free",
                potentialAction: {
                    "@type": "ListenAction",
                    target: url
                }
            }
        }
    })
})

/** CollectionPage for a category hub, wrapping the station ItemList. Tells a
 *  crawler that the page *is* the list — `STATIONS_LD_JSON` on its own says a
 *  list exists somewhere on it, which is weaker. */
export const CATEGORY_LD_COLLECTION = (
    { id, about, title, description, stations, name, dateModified }:
    {
        id: string, about: string, title: string, description: string,
        stations: IStation[], name: string, dateModified: string
    }
) => {
    const url = `${process.env.NEXT_PUBLIC_URL}category/${id}/`
    return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": url,
        url,
        name: title,
        description,
        inLanguage: "he-IL",
        isPartOf: { "@id": WEBSITE_LD_ID },
        publisher: { "@id": ORGANIZATION_LD_ID },
        about,
        // The station list is re-derived on every regeneration, so this is when
        // the page's content was last confirmed — not a hand-typed date.
        dateModified,
        mainEntity: CATEGORY_LD_STATIONS(stations, name)
    }
}

// STATIONS_LD_FAQ was removed. It emitted a FAQPage node with two generic
// questions plus one templated question per city per station — roughly 1,600
// near-duplicates sitewide — none of which was rendered as visible text.
// Google retired FAQ rich results in Aug 2023 except for government and health
// sites, so it earned no SERP feature, and marked-up content that a visitor
// cannot see is against the structured-data policy. The visible Freqs table and
// /frequencies/ already answer every frequency question, better.
//
// LD_FAQ (above) stays: the category pages feed it Q&A that is rendered.
export const STATIONS_LD_BREADBRUMBS = (station: IStation) => {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "רדיו",
            "item": process.env.NEXT_PUBLIC_URL
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": station.name,
            "item": `${process.env.NEXT_PUBLIC_URL}station/${station.id}/`
          }
        ]
      }
}
export const STATIONS_LD_NOW_PLAYING = (station: IStation, songs: ISongs | null) => {
    const current = songs?.current
    if (!current?.title) return null

    const node: Record<string, any> = {
        "@context": "https://schema.org",
        "@type": "MusicRecording",
        name: current.title,
        url: `${process.env.NEXT_PUBLIC_URL}station/${station.id}/`,
    }
    if (current.artist) node.byArtist = { "@type": "MusicGroup", name: current.artist }
    if (current.album) node.inAlbum = { "@type": "MusicAlbum", name: current.album }
    return node
}
// The recently-played list is the one piece of a station page that is genuinely
// unique and refreshes on its own, so it's worth describing to search engines as
// a playlist of real recordings rather than leaving it as loose markup.
export const STATIONS_LD_RECENT_SONGS = (station: IStation, songs: ISongs | null, limit: number) => {
    // Same selector the section renders from — structured data that lists tracks
    // the page doesn't show is worse than none. The track on air is left out
    // here too; it's already described by STATIONS_LD_NOW_PLAYING.
    const history = playlistTracks(songs, limit)
    if (!history.length) return null

    return {
        "@context": "https://schema.org",
        "@type": "MusicPlaylist",
        name: `פלייליסט ${station.name}: איזה שירים הושמעו ב${station.name} לאחרונה?`,
        url: `${process.env.NEXT_PUBLIC_URL}station/${station.id}/#playlist`,
        numTracks: history.length,
        track: history.map(song => ({
            "@type": "MusicRecording",
            name: song.title,
            ...(song.artist ? { byArtist: { "@type": "MusicGroup", name: song.artist } } : {}),
            ...(song.album ? { inAlbum: { "@type": "MusicAlbum", name: song.album } } : {}),
        })),
    }
}
export const STATIONS_LD_ON_AIR = (station: IStation, shows: IShows | null) => {
    const current = shows?.current
    if (!current?.title) return null

    return {
        "@context": "https://schema.org",
        "@type": "BroadcastEvent",
        name: current.title,
        isLiveBroadcast: true,
        ...(current.subtitle ? { description: current.subtitle } : {}),
        ...(shows?.startsAt ? { startDate: new Date(shows.startsAt).toISOString() } : {}),
        ...(shows?.endsAt ? { endDate: new Date(shows.endsAt).toISOString() } : {}),
        publishedOn: { "@type": "RadioChannel", name: station.name },
        url: `${process.env.NEXT_PUBLIC_URL}station/${station.id}/`,
    }
}
export const STATIONS_LD_CHANNEL = (station: IStation) => {
    return {
        "@context": "https://schema.org",
        "@type": "RadioChannel",
        "name": station.name,
        ...(station.freq? {"broadcastFrequency": station.freq.replace("FM", " FM")}: ''),
        "inLanguage": "he",
        "broadcastServiceTier": "Free",
        "providesBroadcastService": {
            "@type": "BroadcastService",
            "name": `${station.name} לייב אונליין`,
            "url": `${process.env.NEXT_PUBLIC_URL}station/${station.id}/`
        }
    }
}
export const STATIONS_LD_PLAYLIST = (station: IStation, playlist: ITrack[]) => {
    return {
        "@context": "https://schema.org",
        "@type": "MusicPlaylist",
        "name": `${station.name} - שירים אחרונים`,
        "description": `האזינו לשירים אחרונים של ${station.name} באתר ${process.env.NEXT_PUBLIC_SITE_NAME} - האזנה מיידית ובחינם לשירים החמים ביותר.`,
        "numTracks": playlist.length,
        "track": playlist.map((track, index) => ({
            "@type": "MusicRecording",
            "name": track.name,
            "byArtist": {
                "@type": "MusicGroup",
                "name": track.artists.join(', ')
            },
            "inAlbum": {
                "@type": "MusicAlbum",
                "name": track.album
            },
            "duration": `PT${Math.floor(track.duration_ms / 60000)}M${Math.floor((track.duration_ms % 60000) / 1000)}S`
        }))
    }
}
export const OPEN_SPOTIFY = (url: string) => {
    const timeout = setTimeout(() => {
        window.open(url, '_blank')
    }, 500)
    window.location.href = url.replace('https://open.spotify.com', 'spotify:/')
    window.addEventListener('blur', () => clearTimeout(timeout))
}